import type { SpreadsheetPasteSpecialMode } from '../protocol/spreadsheetEngineProtocol'
import type { SpreadsheetCommandId } from './commandRegistry'

export type ClipboardCommandDescriptor = {
  id: SpreadsheetCommandId
  action: string
}

export type PasteSpecialCommandDescriptor = ClipboardCommandDescriptor & {
  mode: SpreadsheetPasteSpecialMode
}

export type ClipboardContextMenuEntry =
  | { type: 'separator' }
  | ClipboardCommandDescriptor
  | {
    type: 'paste-special-group'
    label: string
    title: string
    disabled: boolean
    commands: Array<PasteSpecialCommandDescriptor & { disabled: boolean }>
  }

export const PASTE_SPECIAL_COMMANDS: PasteSpecialCommandDescriptor[] = [
  { id: 'paste-values', action: 'paste-values', mode: 'values' },
  { id: 'paste-formulas', action: 'paste-formulas', mode: 'formulas' },
  { id: 'paste-formats', action: 'paste-formats', mode: 'formats' },
  { id: 'paste-transpose', action: 'paste-transpose', mode: 'transpose' },
]

export function pasteSpecialCommandForAction(action: string): PasteSpecialCommandDescriptor | null {
  return PASTE_SPECIAL_COMMANDS.find((command) => command.action === action) ?? null
}

export function buildClipboardContextMenuEntries(args: {
  hasEngineClipboard?: boolean | null
  disabledCommandIds?: Iterable<SpreadsheetCommandId>
}): ClipboardContextMenuEntry[] {
  const hasEngineClipboard = Boolean(args.hasEngineClipboard)
  const disabledCommandIds = new Set(args.disabledCommandIds ?? [])
  return [
    { id: 'cut', action: 'cut' },
    { id: 'copy', action: 'copy' },
    { id: 'paste', action: 'paste' },
    { type: 'separator' },
    {
      type: 'paste-special-group',
      label: 'Paste Special',
      title: hasEngineClipboard
        ? 'Paste from the last range copied through the spreadsheet engine.'
        : 'Copy a range first to enable engine-backed Paste Special.',
      disabled: !hasEngineClipboard,
      commands: PASTE_SPECIAL_COMMANDS.map((command) => ({
        ...command,
        disabled: !hasEngineClipboard || disabledCommandIds.has(command.id),
      })),
    },
  ]
}

export function buildPasteOptionsMenuEntries(args: {
  canPasteSpecial?: boolean | null
}): Array<PasteSpecialCommandDescriptor & { disabled: boolean }> {
  const canPasteSpecial = Boolean(args.canPasteSpecial)
  return PASTE_SPECIAL_COMMANDS.map((command) => ({
    ...command,
    disabled: !canPasteSpecial,
  }))
}
